/**
 * bootstrap.js — one-time native platform initialisation.
 *
 * Runs BEFORE React mounts. Responsibilities are narrow:
 *   1. Mark <html> with `.gc-native` so CSS can key off runtime.
 *   2. Seed the sync tokenStorage from Capacitor Preferences so the
 *      AuthProvider boot flow finds the persisted token even when the
 *      WebView localStorage has been evicted.
 *   3. Apply the correct status-bar style for the current theme.
 *   4. Hide the native splash once the DOM has painted so the user
 *      never sees the launch image lingering after the shell is up.
 *
 * Deep-link handling, back-button routing and network-state banners
 * are all set up INSIDE React (see `useNativeAppLifecycle`). Anything
 * here must be safe to run before AuthProvider / SessionProvider exist.
 */
import { isNative } from './platform.js';
import { hydrateFromNative } from '../auth/tokenStorage.js';
import { applyTheme as applyStatusBarTheme } from './statusBar.js';

function currentThemePreference() {
  try {
    const attr = document?.documentElement?.getAttribute('data-theme');
    if (attr === 'light' || attr === 'dark') return attr;
  } catch { /* ignore */ }
  return 'dark';
}

/**
 * Run the pre-mount native bootstrap. On web this only touches the
 * `.gc-native` class (which stays off) and resolves immediately.
 *
 * Callers should `await` this from main.jsx BEFORE the React root
 * renders — the auth boot flow expects the sync token backing store
 * to be seeded.
 */
export async function bootstrapNative() {
  const native = isNative();
  try {
    if (native) document.documentElement.classList.add('gc-native');
    else document.documentElement.classList.remove('gc-native');
  } catch { /* SSR-safe no-op */ }

  if (!native) return;

  // Seed the sync token store from durable native preferences before
  // the AuthProvider boots. Best-effort — if the plugin call fails the
  // AuthProvider still falls back to localStorage.
  try { await hydrateFromNative(); } catch { /* ignore */ }

  // Apply the status bar style for the resolved theme. The ThemeProvider
  // will call this again whenever the user changes their preference.
  try { await applyStatusBarTheme(currentThemePreference()); } catch { /* ignore */ }

  // Hide the native splash once the first paint is guaranteed. We defer
  // to the next animation frame + a short timeout so the WebView has
  // drawn the shell background before the splash unmounts — this
  // prevents the "black flash" some Android devices show when the
  // splash disappears faster than the app can paint its own background.
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => { SplashScreen.hide({ fadeOutDuration: 200 }).catch(() => {}); }, 50);
        });
      });
    } else {
      setTimeout(() => { SplashScreen.hide({ fadeOutDuration: 200 }).catch(() => {}); }, 250);
    }
  } catch { /* splash plugin missing — nothing to do */ }
}
